// KioskIdleLogout — idle auto-sign-out for shared floor kiosks (scale audit
// L4/A3). Shared tablets stayed signed in indefinitely, so anyone walking up
// inherited the last operator's session.
//
// OFF by default and per-device: a tablet is put into kiosk mode once via
// `?kiosk=1` (persists to localStorage); `?kiosk=0` clears it. NON-kiosk
// devices are completely unaffected — no listeners, no timer, no behaviour
// change. On a kiosk device, IDLE_MINUTES with no touch / key / scroll / click
// ends the shift: signOutEverywhere() removes the session AND clears the
// offline identity snapshots (so a cached token can't keep the next person
// signed in), then a hard reload drops to a clean login screen.
//
// Mounted in the authenticated shell, OUTSIDE every gate (<SharedDeviceSignOuts/>),
// so it only runs while signed in — and still runs while a gate (the consent
// prompt, a tenant-status screen) is showing instead of the app. Inside a gate
// it unmounted with the app, and a kiosk left on the prompt never signed out.
//
// Signs out by the shared path (src/lib/signOutThisDevice.ts), the same one the
// paired-device idle sign-out and the consent prompt's Sign out button take.
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { signOutThisDevice } from '@/lib/signOutThisDevice'

const KIOSK_KEY = 'kf49.kioskMode'
const IDLE_MINUTES = 15
const IDLE_MS = IDLE_MINUTES * 60 * 1000

// Resolve kiosk mode, honouring a `?kiosk=1|0` toggle that persists to
// localStorage so an admin sets a device up once and it sticks across reloads.
function resolveKioskMode(): boolean {
  try {
    const flag = new URLSearchParams(window.location.search).get('kiosk')
    if (flag === '1') localStorage.setItem(KIOSK_KEY, '1')
    else if (flag === '0') localStorage.removeItem(KIOSK_KEY)
    return localStorage.getItem(KIOSK_KEY) === '1'
  } catch {
    return false
  }
}

export default function KioskIdleLogout() {
  // Through a ref, so the effect below runs ONCE: useNavigate() changes on
  // every route change, and re-running the effect would restart the idle timer
  // on a navigation nobody made.
  const nav = useNavigate()
  const navigate = useRef(nav)
  navigate.current = nav
  useEffect(() => {
    if (!resolveKioskMode()) return

    let timer: ReturnType<typeof setTimeout> | undefined
    let signingOut = false

    const onIdle = async () => {
      if (signingOut) return
      signingOut = true
      // Every session + the offline identity, bounded so a dead link cannot
      // hang it, then a hard reload to a clean boot so no in-memory state from
      // the previous operator survives for the next one.
      await signOutThisDevice(navigate.current)
    }

    const reset = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(onIdle, IDLE_MS)
    }

    // Discrete interaction events only — NOT mousemove (cursor drift would keep
    // an unattended kiosk alive forever).
    const EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'click'] as const
    EVENTS.forEach(e => window.addEventListener(e, reset, { passive: true }))
    reset()

    return () => {
      if (timer) clearTimeout(timer)
      EVENTS.forEach(e => window.removeEventListener(e, reset))
    }
  }, [])

  return null
}
